import React, { createContext, useContext, useState, useCallback } from 'react';
import { PodcastGuest } from '@/types/podcastHost';

interface PodcastGuestDialogContextType {
  isOpen: boolean;
  mode: 'create' | 'edit';
  editingGuest: PodcastGuest | null;
  openCreateDialog: () => void;
  openEditDialog: (guest: PodcastGuest) => void;
  closeDialog: () => void;
}

const PodcastGuestDialogContext = createContext<PodcastGuestDialogContextType | undefined>(undefined);

export const PodcastGuestDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingGuest, setEditingGuest] = useState<PodcastGuest | null>(null);

  const openCreateDialog = useCallback(() => {
    setMode('create');
    setEditingGuest(null);
    setIsOpen(true);
  }, []);

  const openEditDialog = useCallback((guest: PodcastGuest) => {
    setMode('edit');
    setEditingGuest(guest);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setEditingGuest(null);
  }, []);

  return (
    <PodcastGuestDialogContext.Provider
      value={{
        isOpen,
        mode,
        editingGuest,
        openCreateDialog,
        openEditDialog,
        closeDialog,
      }}
    >
      {children}
    </PodcastGuestDialogContext.Provider>
  );
};

export const usePodcastGuestDialog = () => {
  const context = useContext(PodcastGuestDialogContext);
  if (!context) {
    throw new Error('usePodcastGuestDialog must be used within PodcastGuestDialogProvider');
  }
  return context;
};
